import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Headphones, Layers, Plus, Search } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import Modal from 'components/ui/Modal';
import { useToast } from 'components/ui/use-toast';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { cn } from 'lib/utils';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import * as Api from 'modules/standardizedExam/api';
import { KIND_LABEL_KEY, LIBRARY_KINDS, isSatKind } from 'modules/standardizedExam/constants';
import type { SectionBlockSummary, SectionKind } from 'modules/standardizedExam/types';

type StatusFilter = 'ALL' | 'READY' | 'DRAFT' | 'ARCHIVED';

const STATUS_STYLE: Record<string, string> = {
  READY: 'bg-success-50 text-success-700 border-success-200',
  DRAFT: 'bg-muted text-muted-foreground border-border',
  ARCHIVED: 'bg-muted text-muted-foreground border-border line-through',
};

/**
 * P10 section library: reusable IELTS sections and SAT module sets. Tests
 * with random slots draw from the ready ones, so each student can get a
 * different full paper.
 */
export const SectionLibraryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const hasIelts = useFeature(FEATURES.IELTS_SUITE);
  const hasSat = useFeature(FEATURES.SAT_SUITE);
  const upgrade = useOpenUpgradeForFeature();
  const [kind, setKind] = useState<SectionKind | 'ALL'>('ALL');
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const { input, setInput, query } = useSearchParamState();
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newKind, setNewKind] = useState<SectionKind>(hasIelts ? 'IELTS_READING' : 'SAT_RW');
  const [saving, setSaving] = useState(false);

  const { data = [], isLoading } = useQuery({
    queryKey: ['section-library', 'list', kind, status, query],
    queryFn: async () =>
      (
        await Api.LibraryList({
          ...(kind !== 'ALL' ? { kind } : {}),
          ...(status !== 'ALL' ? { status } : {}),
          ...(query ? { q: query } : {}),
        })
      ).data.data,
  });

  const allowedKinds = LIBRARY_KINDS.filter((k) => (isSatKind(k) ? hasSat : hasIelts));
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const b of data) map.set(b.kind, (map.get(b.kind) ?? 0) + 1);
    return map;
  }, [data]);

  const create = async () => {
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      const { data: created } = await Api.LibraryCreate({ title: newTitle.trim(), kind: newKind });
      navigate(`/app/section-library/${created.id}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('failed_to_save'));
    } finally {
      setSaving(false);
    }
  };

  if (!hasIelts && !hasSat) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <Layers className="mx-auto h-10 w-10 text-muted-foreground" aria-hidden />
        <h1 className="mt-4 font-display text-2xl font-bold">{t('exam.library.title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('exam.library.upgrade_body')}</p>
        <Button className="mt-6" onClick={() => upgrade(FEATURES.IELTS_SUITE)}>
          {t('exam.library.upgrade')}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground">{t('exam.library.title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('exam.library.subtitle')}</p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-1.5">
          <Plus className="h-4 w-4" aria-hidden /> {t('exam.library.new')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('exam.library.search')} className="pl-9" aria-label={t('exam.library.search')} />
        </div>
        <div role="tablist" aria-label={t('exam.library.status_filter')} className="flex rounded-md border border-border p-0.5 text-sm">
          {(['ALL', 'READY', 'DRAFT', 'ARCHIVED'] as const).map((s) => (
            <button
              key={s}
              role="tab"
              aria-selected={status === s}
              onClick={() => setStatus(s)}
              className={cn('rounded px-3 py-1', status === s ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground')}
            >
              {t(`exam.library.status.${s.toLowerCase()}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label={t('exam.library.kind_filter')}>
        {(['ALL', ...allowedKinds] as const).map((k) => (
          <button
            key={k}
            role="tab"
            aria-selected={kind === k}
            onClick={() => setKind(k as SectionKind | 'ALL')}
            className={cn(
              'rounded-full border px-3 py-1 text-sm font-medium',
              kind === k ? 'border-foreground bg-foreground text-background' : 'border-border text-foreground hover:bg-muted',
            )}
          >
            {k === 'ALL' ? t('exam.library.all_kinds') : t(KIND_LABEL_KEY[k as SectionKind])}
            {k !== 'ALL' && kind === 'ALL' && counts.get(k) ? <span className="ml-1.5 opacity-70">{counts.get(k)}</span> : null}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-36 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : data.length ? (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((b) => (
            <BlockCard key={b.id} block={b} />
          ))}
        </ul>
      ) : (
        <div className="rounded-xl border border-dashed border-border px-6 py-12 text-center">
          <p className="font-medium text-foreground">{t('exam.library.empty_title')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('exam.library.empty_body')}</p>
        </div>
      )}

      <Modal isOpen={creating} onClose={() => setCreating(false)} title={t('exam.library.new')} size="md">
        <div className="space-y-4">
          <label className="block space-y-1.5 text-sm">
            <span className="font-medium">{t('exam.library.name')}</span>
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder={t('exam.library.name_placeholder')} autoFocus />
          </label>
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">{t('exam.library.kind')}</legend>
            <div className="grid grid-cols-2 gap-2">
              {allowedKinds.map((k) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={newKind === k}
                  onClick={() => setNewKind(k)}
                  className={cn('rounded-md border px-3 py-2 text-left text-sm', newKind === k ? 'border-foreground bg-muted font-semibold' : 'border-border')}
                >
                  {t(KIND_LABEL_KEY[k])}
                </button>
              ))}
            </div>
            {isSatKind(newKind) && <p className="text-xs text-muted-foreground">{t('exam.library.set_note')}</p>}
          </fieldset>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCreating(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={create} disabled={!newTitle.trim() || saving}>
              {saving ? t('saving') : t('exam.library.create')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

function BlockCard({ block }: { block: SectionBlockSummary }) {
  const { t } = useTranslation();
  return (
    <li>
      <Link
        to={`/app/section-library/${block.id}`}
        className="flex h-full flex-col rounded-xl border border-border bg-card p-4 transition-colors hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs font-semibold text-muted-foreground">{block.code}</span>
          <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-semibold', STATUS_STYLE[block.status])}>
            {t(`exam.library.status.${block.status.toLowerCase()}`)}
          </span>
        </div>
        <p className="mt-2 line-clamp-2 font-semibold text-foreground">{block.title}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t(KIND_LABEL_KEY[block.kind])}
          {block.difficulty && ` · ${t(`exam.diff.${block.difficulty}`)}`}
          {block.version > 1 && ` · v${block.version}`}
        </p>
        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 text-xs text-muted-foreground">
          <span>{t('exam.library.n_questions', { count: block.questionCount })}</span>
          {block.hasAudio && (
            <span className="flex items-center gap-1">
              <Headphones className="h-3 w-3" aria-hidden /> {t('exam.library.has_audio')}
            </span>
          )}
          <span>{t('exam.library.used', { count: block.usageCount })}</span>
          {block.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="rounded bg-muted px-1.5 py-0.5">
              {tag}
            </span>
          ))}
        </div>
      </Link>
    </li>
  );
}

export default SectionLibraryPage;
